import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import Styles from './addressProof.module.css'
import FileInput from '../../../components/Input/FileInput/FileInput'
import Input from '../../../components/Input/Input'
import Button from '../../../components/Button/Button'
import { showErrorSnackbar, showSuccessSnackbar } from '../../snackbar/helpers'
import { _uploadAddressProof } from '../redux/actions'

function AddressProof({ email, setStep }) {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [files, setFiles] = useState([])
  const [formData, setFormData] = useState({
    doc_type: '',
    doc_date: '',
    address: '',
    postcode: '',
    city: '',
    country: ''
    // identificator: token,
    // doc_category: null
  })
  const [errors, setErrors] = useState({})

  const handleFileChange = newFiles => {
    setFiles(newFiles)
    setErrors({ ...errors, files: '' })
  }

  // Update the handleInputChange function to handle both regular inputs and selects
  // Update the handleInputChange function to handle both regular inputs and country select
  const handleInputChange = e => {
    // Handle country select differently as it passes the country code directly
    if (typeof e === 'string') {
      setFormData(prev => ({
        ...prev,
        country: e
      }))
      setErrors(prev => ({
        ...prev,
        country: ''
      }))
      return
    }

    // Handle regular input fields
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }))
  }

  const handleSelectChange = e => {
    // console.log(e.target.tagName)
    setFormData(prev => ({
      ...prev,
      doc_type: e
    }))
    setErrors(prev => ({
      ...prev,
      doc_type: ''
    }))
  }

  const handleCountryChange = e => {
    // console.log(e)
    setFormData(prev => ({
      ...prev,
      country: e.target.value
    }))
    setErrors(prev => ({
      ...prev,
      country: ''
    }))
  }

  // Add specific handler for date changes
  const handleDateChange = date => {
    setFormData(prev => ({
      ...prev,
      doc_date: date
    }))
    // Clear any existing date error when the user changes the date
    setErrors(prev => ({
      ...prev,
      doc_date: ''
    }))
  }

  const isDateWithinThreeMonths = dateStr => {
    if (!dateStr) return false

    const docDate = new Date(dateStr)
    const today = new Date()

    // Calculate the date 3 months ago
    const threeMonthsAgo = new Date()
    threeMonthsAgo.setMonth(today.getMonth() - 3)

    // Check if the document date is after (more recent than) the date 3 months ago
    return docDate >= threeMonthsAgo
  }

  const validateForm = () => {
    // console.log(formData.country)
    // console.log(formData.city)
    const newErrors = {}

    if (files.length === 0) {
      newErrors.files = 'Please upload at least one proof of address document'
    }

    const validTypes = ['image/jpeg', 'image/png', 'application/pdf']
    const isValidType = files.every(file => validTypes.includes(file.type))
    if (!isValidType) {
      newErrors.files = 'Please upload only JPG, PNG or PDF files'
    }

    const maxSize = 5 * 1024 * 1024 // 5MB
    const isValidSize = files.every(file => file.size <= maxSize)
    if (!isValidSize) {
      newErrors.files = 'Each file must be less than 5MB'
    }

    if (!formData.doc_type) newErrors.doc_type = 'Document type is required'
    if (!formData.address) newErrors.address = 'Address is required'
    if (!formData.city) newErrors.city = 'City is required'
    if (!formData.country) newErrors.country = 'Country is required'
    if (!formData.postcode) newErrors.postcode = 'Postal code is required'

    // Validate document date
    if (!formData.doc_date) {
      newErrors.doc_date = 'Document date is required'
    } else if (!isDateWithinThreeMonths(formData.doc_date)) {
      newErrors.doc_date = 'Document must not be older than 3 months'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    const submitData = new FormData()

    // Append files
    files.forEach(file => {
      submitData.append('upload[]', file)
    })

    // Append form data
    Object.keys(formData).forEach(key => {
      submitData.append(key, formData[key])
    })

    try {
      await _uploadAddressProof(
        submitData,
        response => {
          showSuccessSnackbar({
            alertMessage: 'Address proof documents uploaded successfully'
          })
          setStep(3) // Move to next step (Verification Form)
        },
        error => {
          console.log(error)
          showErrorSnackbar({
            alertMessage: error.errors[0]?.length ? (
              <FormattedMessage id={error.errors[0]} />
            ) : (
              'Failed to upload documents'
            )
          })
          setLoading(false)
        }
      )
    } catch (err) {
      setLoading(false)
      showErrorSnackbar({
        alertMessage: 'An error occurred while uploading documents'
      })
    }
  }

  return (
    <div className={Styles.formContainer}>
      <div className={Styles.stepNumberContainer}>
        <div>2</div>
        <div>
          <span>Proof of Address</span>
          <span>Please provide your address details and supporting documents</span>
        </div>
      </div>

      <div className={Styles.formSectionTitle}>
        <h5>Upload Documents</h5>
      </div>

      <div className={Styles.uploadSection}>
        <FileInput
          type='file'
          inputLabel='Upload Address Proof'
          variant='secondary'
          onFileChange={handleFileChange}
          multiple={true}
          maxFiles={3}
          accept={['.jpg', '.jpeg', '.png', '.pdf', '.docx']}
          error={!!errors.files}
        />
        {errors.files && <div className={Styles.error}>{errors.files}</div>}
      </div>

      <ul className={Styles.documentList}>
        <li>
          <FormattedMessage
            id='kyc.address.proof.utility'
            defaultMessage='Utility bill (electricity, water, gas) - not older than 3 months'
          />
        </li>
        <li>
          <FormattedMessage id='kyc.address.proof.bank' defaultMessage='Bank statement - not older than 3 months' />
        </li>
        <li>
          <FormattedMessage
            id='kyc.address.proof.government'
            defaultMessage='Government-issued document showing your address'
          />
        </li>
      </ul>

      <div className={Styles.formSectionTitle}>
        <h5>Document Details</h5>
      </div>

      <form>
        <Input
          type='select'
          name='doc_type'
          inputLabel='Document Type'
          onChange={(e, value) => handleSelectChange(value)}
          selectValue={formData.doc_type}
          error={{ message: errors.doc_type }}
          placeholder='e.g., Bank Statement, Utility Bill, etc..'
          selectOptions={[
            { value: '', label: 'Select Document Type' },
            { value: 'utility_bill', label: 'Utility Bill' },
            { value: 'bank_statement', label: 'Bank Statement' },
            { value: 'lease_agreement', label: 'Lease Agreement' },
            { value: 'birth_certificate', label: 'Birth Certificate' },
            { value: 'government_issued_document', label: 'Government Issued Document' },
            { value: 'salary_slip', label: 'Salary slip' },
            { value: 'other', label: 'Other' }
          ]}
          inputIconContainerStyle={{
            border: '1px var(--border-color) solid',
            borderRadius: 5,
            background: 'var(--secondary-color)',
            marginBottom: '20px'
          }}
          required
        />

        <Input
          type='date'
          name='doc_date'
          inputLabel='Document Issued Date'
          value={formData.doc_date}
          inputIconContainerStyle={{
            border: '1px var(--border-color) solid',
            borderRadius: 5,
            background: 'var(--secondary-color)',
            marginBottom: '20px'
          }}
          onChange={handleDateChange}
          setDate={handleDateChange}
          error={{ message: errors.doc_date }}
          errorMessage={errors.doc_date}
          selectedDate={formData.doc_date}
          required
        />
        {errors.doc_date && <div className={Styles.error}>{errors.doc_date}</div>}
      </form>

      <div className={Styles.formSectionTitle}>
        <h5>Address Information</h5>
      </div>

      <div>
        <Input
          type='text'
          name='address'
          inputLabel='Street Address'
          placeholder='Enter your full street address'
          value={formData.address}
          onChange={handleInputChange}
          inputIconContainerStyle={{
            border: '1px var(--border-color) solid',
            borderRadius: 5,
            background: 'var(--secondary-color)',
            marginBottom: '20px'
          }}
          error={{ message: errors.address }}
          errorMessage={errors.address}
          required
        />

        <Input
          type='text'
          name='city'
          inputLabel='City'
          placeholder='Enter city name'
          value={formData.city}
          inputIconContainerStyle={{
            border: '1px var(--border-color) solid',
            borderRadius: 5,
            background: 'var(--secondary-color)',
            marginBottom: '20px'
          }}
          onChange={handleInputChange}
          error={{ message: errors.city }}
          errorMessage={errors.city}
          required
        />

        <Input
          type='country'
          name='country'
          inputLabel='Country'
          placeholder='Enter country name'
          value={formData.country}
          onChange={handleCountryChange}
          error={{ message: errors.country }}
          errorMessage={errors.country}
          required
        />

        <Input
          type='text'
          name='postcode'
          inputLabel='Postal Code'
          placeholder='Enter postal code'
          value={formData.postcode}
          onChange={handleInputChange}
          inputIconContainerStyle={{
            border: '1px var(--border-color) solid',
            borderRadius: 5,
            background: 'var(--secondary-color)',
            marginBottom: '20px'
          }}
          error={{ message: errors.postcode }}
          errorMessage={errors.postcode}
          required
        />
      </div>

      <Button
        text={loading ? 'Uploading...' : 'Submit Documents'}
        onClick={handleSubmit}
        disabled={loading}
        style={{
          padding: '10px 20px',
          marginTop: '30px',
          width: '100%',
          maxWidth: '300px'
        }}
      />
    </div>
  )
}

export default AddressProof
